import './RulesFormModal.scss';
import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import {
  ChangeEvent,
  Dispatch,
  MouseEvent,
  MutableRefObject,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Col, Form, InputGroup, Modal, Row } from 'react-bootstrap';
import { Button } from '@Components/Button/Button';
import { Appearance, Design } from '@Components/designConstants';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { ValidationErrors } from '../../Validation/ValidationErrors';
import { WtoTab, WtoTabType } from '../../web-traffic-optimization-constants';
import {
  defaultRule,
  FormContext,
  WtoCookieOperation,
  WtoRule,
  WtoUtmOperation,
  WtoUtmUrl,
  WtoVariant
} from '../../wto-form-context';
import {
  parseTimestampCustomRetention,
  parseTimestampDowOperations,
  parseTimestampTimeframe
} from '../../wto-utils';
import { ConditionsSection } from './ConditionsSection';
import { CookieSection } from './CookieSection';
import { createCustomRetentionHandlers } from './custom-retention-handlers';
import { DayOfWeekSection } from './DayOfWeekSection';
import { createDowHandlers } from './dow-handlers';
import { DATE_FORMAT, RETENTION_MAX_YEAR, RETENTION_MIN_YEAR } from './rules-form-modal-constants';
import { isVariantOption } from './rules-form-modal-utils';
import { createTimeframeHandlers } from './timeframe-handlers';
import { TimeframeSection } from './TimeframeSection';
import { TrafficAllocationSection } from './TrafficAllocationSection';
import { UrlParamsSection } from './UrlParamsSection';
import { createValidateRule } from './validate-rule';

interface RulesFormModalProps {
  show: boolean;
  closeModal: () => void;
  readOnly: boolean;
  goToTab: (tab: WtoTabType) => void;
  ruleIndex: number | null;
  showRuleCards: boolean[];
  setShowRuleCards: Dispatch<SetStateAction<boolean[]>>;
  isCancellingRef: MutableRefObject<boolean>;
}

export const RulesFormModal = ({
  show,
  closeModal,
  readOnly,
  goToTab,
  ruleIndex,
  showRuleCards,
  setShowRuleCards,
  isCancellingRef
}: RulesFormModalProps) => {
  const { wtoFormObj, setWtoFormObj, newRule, setNewRule } = useContext(FormContext);

  const today = Dfns.format(DATE_FORMAT)(new Date());
  const DEFAULT_DATE = Dfns.format(DATE_FORMAT)(new Date());

  const savedTimeData = useMemo(
    () => ({
      savedCustomRetention: parseTimestampCustomRetention(DEFAULT_DATE, newRule.customRetention),
      savedTimeframe: parseTimestampTimeframe(DEFAULT_DATE, newRule.timeframe),
      savedDow: parseTimestampDowOperations(DEFAULT_DATE, newRule.dow_operation)
    }),
    [newRule, DEFAULT_DATE]
  );

  const { savedCustomRetention, savedTimeframe, savedDow } = savedTimeData;

  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const {
    toggleCustomRetention,
    handleCustomRetentionDate,
    handleCustomRetentionTimezone,
    handleCustomRetentionTime,
    handleCustomRetentionPeriod
  } = createCustomRetentionHandlers(newRule, setNewRule, savedCustomRetention);

  const {
    toggleTimeframe,
    toggleTimeframeParam,
    updateTimeframeId,
    handleTimeframeDates,
    handleTimeframeTimezone,
    handleTimeframeTime,
    handleTimeframePeriod
  } = createTimeframeHandlers(newRule, setNewRule, savedTimeframe);

  const {
    handleDowTimezone,
    handleDowTime,
    handleDowPeriod,
    addDayOfWeekSplitRow,
    addDowTrafficSplit,
    handleDowVariant,
    selectDay
  } = createDowHandlers(newRule, setNewRule, savedDow, wtoFormObj, readOnly, setValidationErrors);

  const { validateRule } = createValidateRule(
    newRule,
    wtoFormObj,
    ruleIndex,
    showRuleCards,
    setShowRuleCards,
    setWtoFormObj,
    setValidationErrors,
    closeModal
  );

  useEffect(() => {
    if (isCancellingRef.current) {
      return;
    }
    const invalidId = (wtoFormObj.variants as WtoVariant[]).find(
      variant => variant.variant_id === ''
    );
    const invalidUrl = (wtoFormObj.variants as WtoVariant[]).find(
      variant => variant.variant_url === ''
    );
    if (invalidId || invalidUrl) {
      goToTab(WtoTab.ROUTE_DETAILS);
    }
  }, [newRule, wtoFormObj, goToTab, isCancellingRef]);

  // keyName narrows which shape `event` is: a plain string for the *_operation cases (an
  // operation name from a dropdown), or a { variant_id, variant_url } option object otherwise.
  const onSelectChange = (
    event: unknown,
    keyName?: 'additional_utm' | 'additional_utm_operation' | 'variant_id' | 'cookie_operation',
    index?: number,
    index2?: number
  ) => {
    const newRuleObj: WtoRule = R.clone(newRule);
    switch (keyName) {
      case 'additional_utm':
        if ((index || index === 0) && isVariantOption(event)) {
          let utmOperations: WtoUtmUrl[] = [];
          if (event.variant_id.toLocaleLowerCase() === 'all' && Array.isArray(event.variant_url)) {
            utmOperations = event.variant_url.map(
              (utmOp: { variant_id: string; variant_url: string | string[] }) => ({
                utm_variant_id: utmOp.variant_id,
                utm_variant_url: utmOp.variant_url
              })
            );
          } else {
            utmOperations.push({
              utm_variant_id: event.variant_id,
              utm_variant_url: event.variant_url as string | string[]
            });
          }
          newRuleObj.additional_utm[index].additional_utm_url = utmOperations;
        }
        break;
      case 'additional_utm_operation':
        if ((index || index === 0) && index2 !== undefined && typeof event === 'string') {
          newRuleObj.additional_utm[index2].additional_utm_operation[index] = {
            operation: '',
            utm_key: '',
            utm_value: ''
          };
          if (event === 'Replace') {
            newRuleObj.additional_utm[index2].additional_utm_operation[index].replace_key = '';
            newRuleObj.additional_utm[index2].additional_utm_operation[index].replace_value = '';
          } else {
            delete newRuleObj.additional_utm[index2].additional_utm_operation[index].replace_key;
            delete newRuleObj.additional_utm[index2].additional_utm_operation[index].replace_value;
          }
          newRuleObj.additional_utm[index2].additional_utm_operation[index].operation = event;
        }
        break;
      case 'variant_id':
        if ((index || index === 0) && isVariantOption(event)) {
          newRuleObj.variant_splits[index].variant_id = event.variant_id;
        }
        newRuleObj.cookie_operation_url = [];
        break;
      case 'cookie_operation':
        if ((index || index === 0) && typeof event === 'string') {
          newRuleObj.cookie_operation[index] = {
            cookie_key: '',
            cookie_value: '',
            operation: ''
          };
          if (event === 'Replace') {
            newRuleObj.cookie_operation[index].cki_replace_key = '';
            newRuleObj.cookie_operation[index].cki_replace_value = '';
          } else {
            delete newRuleObj.cookie_operation[index].cki_replace_key;
            delete newRuleObj.cookie_operation[index].cki_replace_value;
          }
          newRuleObj.cookie_operation[index].operation = event;
        }
        break;
      default:
        break;
    }
    setNewRule(newRuleObj);
  };

  const onInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    valueType?: string,
    idx?: number
  ) => {
    const newRuleObj: WtoRule = R.clone(newRule);
    if (valueType === 'rule_name') {
      newRuleObj.rule_name = e.target.value;
    } else if (idx !== undefined) {
      switch (e.target.dataset.type) {
        case 'utm_operation': {
          const utmIndex = Number(e.target.dataset.utmindex);
          // valueType is one of utm_key/utm_value/replace_key/replace_value at this call site
          newRuleObj.additional_utm[utmIndex].additional_utm_operation[idx][
            valueType as keyof WtoUtmOperation
          ] = e.target.value;
          break;
        }
        case 'split': {
          let splitValue = Number(e.target.value);
          if (splitValue > 100) {
            splitValue = 100;
          } else if (splitValue < 0) {
            splitValue = 0;
          }
          newRuleObj.variant_splits[idx].split = splitValue;
          break;
        }
        case 'cookie_operation':
          // valueType is one of cookie_key/cookie_value/cki_replace_key/cki_replace_value here
          newRuleObj.cookie_operation[idx][valueType as keyof WtoCookieOperation] = e.target.value;
          break;
        default:
          break;
      }
    }
    setNewRule(newRuleObj);
  };

  const updateRuleField = (key, value) =>
    setNewRule((current: any) => ({ ...current, [key]: value }));

  const addCondition = () => {
    const newRuleFormObj = R.clone(newRule);
    newRuleFormObj.conditions = [
      ...newRule.conditions,
      {
        operator: newRuleFormObj.conditions.length === 0 ? 'WHEN' : 'AND',
        trigger: '',
        rule: '',
        value: ''
      }
    ];
    setNewRule(newRuleFormObj);
  };

  const onDeleteOperationClick = (
    e: MouseEvent<HTMLElement>,
    ruleType:
      | 'conditions'
      | 'cookie_operation'
      | 'additional_utm'
      | 'additional_utm_operation'
      | 'variant_splits',
    optrnIdx?: number
  ) => {
    const newRuleFormObj = R.clone(newRule);
    const index = Number(e.currentTarget.dataset.index);
    if (ruleType === 'additional_utm_operation') {
      newRuleFormObj.additional_utm[index][ruleType].splice(optrnIdx, 1);
    } else {
      newRuleFormObj[ruleType].splice(index, 1);
    }
    if (ruleType === 'conditions' && newRuleFormObj.conditions.length > 0) {
      newRuleFormObj.conditions[0].operator = 'WHEN';
    }
    if (ruleType === 'variant_splits') {
      newRuleFormObj.cookie_operation_url = [];
    }
    setNewRule(newRuleFormObj);
  };

  const addParameterUpdatesRow = (ruleType: string): void => {
    const newRuleFormObj = R.clone(newRule);

    switch (ruleType) {
      case 'additional_utm':
        newRuleFormObj.additional_utm = [
          ...newRule.additional_utm,
          {
            additional_utm_operation: [],
            additional_utm_url: []
          }
        ];
        break;
      case 'cookie_operation':
        newRuleFormObj.cookie_operation = [
          ...newRule.cookie_operation,
          {
            cookie_key: '',
            cookie_value: '',
            operation: ''
          }
        ];
        break;
      default:
        break;
    }
    setNewRule(newRuleFormObj);
  };

  const addParameterOperation = (index, typeblock) => {
    const newRuleFormObj = R.clone(newRule);
    newRuleFormObj.additional_utm[index][typeblock] = [
      ...newRuleFormObj.additional_utm[index][typeblock],
      {
        operation: '',
        utm_key: '',
        utm_value: ''
      }
    ];
    setNewRule(newRuleFormObj);
  };

  const addTrafficSplit = () => {
    if (newRule.variant_splits.length < wtoFormObj.variants.length) {
      updateRuleField('variant_splits', [...newRule.variant_splits, { split: 0, variant_id: '' }]);
    }
  };

  const cancelRuleCreation = () => {
    setNewRule(R.clone(defaultRule));
    closeModal();
  };

  return (
    <Modal
      show={show}
      onHide={cancelRuleCreation}
      className='rules-form-modal'
      data-testid='rules-form-modal'
    >
      <Modal.Header closeButton>
        <Modal.Title>Route Rules</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ValidationErrors validationErrors={validationErrors} />
        <Form className='form'>
          <Row className='rule-name-wrapper'>
            <Form.Group as={Col}>
              <InputGroup>
                <Form.Control
                  value={newRule.rule_name}
                  disabled={readOnly}
                  placeholder='Rule Name'
                  onChange={e => onInputChange(e, 'rule_name')}
                />
              </InputGroup>
            </Form.Group>
            <Col xs='auto'>
              <ToggleSwitch
                disabled={readOnly}
                label='Timeframe'
                checked={newRule.timeframe_enabled}
                onChange={toggleTimeframe}
              />
            </Col>
          </Row>
          <TrafficAllocationSection
            newRule={newRule}
            wtoFormObj={wtoFormObj}
            readOnly={readOnly}
            minYear={RETENTION_MIN_YEAR}
            maxYear={RETENTION_MAX_YEAR}
            onSelectChange={onSelectChange}
            onInputChange={onInputChange}
            onDeleteOperationClick={onDeleteOperationClick}
            handleDowVariant={handleDowVariant}
            addTrafficSplit={addTrafficSplit}
            addDowTrafficSplit={addDowTrafficSplit}
            toggleCustomRetention={toggleCustomRetention}
            handleCustomRetentionDate={handleCustomRetentionDate}
            handleCustomRetentionTime={handleCustomRetentionTime}
            handleCustomRetentionPeriod={handleCustomRetentionPeriod}
            handleCustomRetentionTimezone={handleCustomRetentionTimezone}
          />
          <ConditionsSection
            newRule={newRule}
            setNewRule={setNewRule}
            readOnly={readOnly}
            onDeleteOperationClick={onDeleteOperationClick}
            addCondition={addCondition}
          />
          <UrlParamsSection
            newRule={newRule}
            wtoFormObj={wtoFormObj}
            readOnly={readOnly}
            onInputChange={onInputChange}
            onSelectChange={onSelectChange}
            onDeleteOperationClick={onDeleteOperationClick}
            addParameterUpdatesRow={addParameterUpdatesRow}
            addParameterOperation={addParameterOperation}
          />
          <CookieSection
            newRule={newRule}
            setNewRule={setNewRule}
            wtoFormObj={wtoFormObj}
            readOnly={readOnly}
            onInputChange={onInputChange}
            onSelectChange={onSelectChange}
            onDeleteOperationClick={onDeleteOperationClick}
            addParameterUpdatesRow={addParameterUpdatesRow}
          />
          {newRule.timeframe_enabled && newRule.timeframe && (
            <TimeframeSection
              newRule={{ ...newRule, timeframe: newRule.timeframe }}
              wtoFormObj={wtoFormObj}
              readOnly={readOnly}
              today={today}
              handleTimeframeDates={handleTimeframeDates}
              handleTimeframeTime={handleTimeframeTime}
              handleTimeframePeriod={handleTimeframePeriod}
              handleTimeframeTimezone={handleTimeframeTimezone}
              toggleTimeframeParam={toggleTimeframeParam}
              updateTimeframeId={updateTimeframeId}
            />
          )}
          <DayOfWeekSection
            newRule={newRule}
            wtoFormObj={wtoFormObj}
            readOnly={readOnly}
            selectDay={selectDay}
            handleDowTime={handleDowTime}
            handleDowPeriod={handleDowPeriod}
            handleDowTimezone={handleDowTimezone}
            handleDowVariant={handleDowVariant}
            addDayOfWeekSplitRow={addDayOfWeekSplitRow}
            addDowTrafficSplit={addDowTrafficSplit}
            onSelectChange={onSelectChange}
            onInputChange={onInputChange}
            onDeleteOperationClick={onDeleteOperationClick}
            addTrafficSplit={addTrafficSplit}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          appearance={Appearance.OUTLINED}
          design={Design.NEUTRAL}
          onClick={cancelRuleCreation}
        >
          Cancel
        </Button>
        <Button onClick={() => validateRule()}>Save Rule</Button>
      </Modal.Footer>
    </Modal>
  );
};
